import React from 'react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormProvider, TextField } from 'components/Form';
import { Button, Stack, Typography } from '@mui/material';
import { RedMessageBar } from 'components/RedMessageBar';
import { PaperCard } from 'components/Card';
import { paths } from 'routes';
import useYupValidationResolver from 'hook/useYupValidationResolver';
import { resetPasswordSchema } from '../validation';
import { useAuth } from 'lib/auth';

export const ResetPasswordForm = ({ onSuccess, actionCode }) => {
  const { t } = useTranslation();
  const { resetPassword, verifyResetCode, login } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const methods = useForm({
    mode: 'onSubmit',
    resolver: useYupValidationResolver(resetPasswordSchema),
    values: { password: password, confirmPassword: confirmPassword },
  });

  const onSubmit = async () => {
    try {
      await resetPassword(actionCode, password);

      const data = { companyEmail: email, password: password };
      await login(data);

      onSuccess();
    } catch (error) {
      methods.setError('confirmPassword', { type: 'custom', message: error.message });
    }
  };

  useEffect(() => {
    const verifyCode = async () => {
      try {
        const data = await verifyResetCode(actionCode);
        if (data) {
          setEmail(data);
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    verifyCode();
  }, []);

  const titleMargin = 3;
  return (
    <>
      {!loading && (
        <PaperCard px={{ xs: 2, sm: 4 }} width={{ xs: 1, sm: 368 }} maxWidth={{ sm: 368 }} sx={{ my: 5 }}>
          <Stack>
            <Typography variant="h5" sx={{ mb: titleMargin, textAlign: "center" }}>
              {t('website:auth.resetPassword.heading')}
            </Typography>
            {error && (
              <>
                <RedMessageBar message={error} />
                <Button
                  size="large"
                  variant="contained"
                  color="primary"
                  sx={{
                    mt: 2,
                    mb: 0.5,
                    bgcolor: "primary.light",
                    '&:hover': {
                      backgroundColor: "primary.light",
                    },
                  }}
                  href={paths.homepage}
                >
                  {t('website:auth.resetPassword.returnToHome')}
                </Button>
              </>
            )}

            {!error && (
              <>
                <FormProvider methods={methods} onSubmit={onSubmit}>
                  <Stack>
                    <TextField
                      name="password"
                      label={t('website:auth.resetPassword.passwordLabel')}
                      placeholder={t('website:auth.resetPassword.passwordPlaceholder')}
                      required={true}
                      type="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                      }}
                      error={Boolean(methods?.formState?.errors?.password)}
                      helperText={methods?.formState?.errors?.password?.message}
                      sx={{ mb: 2 }}
                      fullWidth
                    />
                    <TextField
                      name="confirmPassword"
                      label={t('website:auth.resetPassword.confirmPasswordLabel')}
                      placeholder={t('website:auth.resetPassword.confirmPasswordPlaceholder')}
                      required={true}
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                      }}
                      error={Boolean(methods?.formState?.errors?.confirmPassword)}
                      helperText={methods?.formState?.errors?.confirmPassword?.message}
                      sx={{ mb: 1 }}
                      fullWidth
                    />

                    <Button
                      size="large"
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{
                        mt: 2,
                        mb: 0.5,
                        bgcolor: "primary.light",
                        '&:hover': {
                          backgroundColor: "primary.light",
                        },
                      }}
                    >
                      {t('website:auth.resetPassword.submitButton')}
                    </Button>
                  </Stack>
                </FormProvider>
              </>
            )}
          </Stack>
        </PaperCard>
      )}
    </>
  );
};
